// src/hooks/useCollabMeasurements.js
import * as Y from "yjs";
import { WebsocketProvider } from "y-websocket";
import { useEffect, useRef } from "react";

/**
 * Real-time collaboration for a single `measurements` array.
 * Usage is the SAME as your comments hook:
 *   setMeasurements(next);
 *   syncMeasurements(next);
 */
export default function useCollabMeasurements(projectId, onRemoteUpdate) {
  const ydocRef = useRef(null);
  const providerRef = useRef(null);
  const yArrRef = useRef(null);

  useEffect(() => {
    if (!projectId) return;

    const ydoc = new Y.Doc();
    const raw = process.env.REACT_APP_SOCKET_URL || "ws://localhost:1234";
    const socketUrl = raw.replace(/['"\s]+/g, "").replace(/\/+$/g, "");

    // ✅ Room/id is NOT part of the URL. It is the 2nd arg.
    const room = String(projectId).trim();

    // IMPORTANT: room id is the 2nd arg; do NOT append projectId in the URL
    const provider = new WebsocketProvider(socketUrl, room, ydoc);

    const yArr = ydoc.getArray("measurements");

    const observer = () => {
      onRemoteUpdate?.(yArr.toArray());
    };

    yArr.observe(observer);
    ydocRef.current = ydoc;
    providerRef.current = provider;
    yArrRef.current = yArr;

    return () => {
      yArr.unobserve(observer);
      provider.destroy();
      ydoc.destroy();
    };
  }, [projectId]);

  // Push entire array (simple replace — same as comments)
  const syncMeasurements = (updated) => {
    const yArr = yArrRef.current;
    if (!yArr) return;
    yArr.delete(0, yArr.length);
    if (Array.isArray(updated) && updated.length) {
      yArr.push(updated);
    }
  };

  return { syncMeasurements };
}
